import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Lock, User, Phone, Store, Bike, Users, ArrowRight, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PhoneInput, { buildE164, isValidNational } from "@/components/PhoneInput";
import AppleLoginButton from "@/components/AppleLoginButton";
import { isNativeApp } from "@/utils/platform";
import { startGoogleLogin, startNativeGoogleLogin } from "@/utils/googleOauth";

const ROLES = [
  { key: "client", icon: Users },
  { key: "restaurant", icon: Store },
  { key: "driver", icon: Bike },
];

const PARTNER_PATHS = { restaurant: "/devenir-partenaire", driver: "/devenir-livreur" };

export default function Register() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const initialRole = params.get("role") || "client";
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: initialRole });
  const [phoneCode, setPhoneCode] = useState("224");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // If user landed here via ?role=restaurant or ?role=driver, redirect to the
  // dedicated candidature page (self-serve account creation is client-only).
  useEffect(() => {
    if (initialRole === "restaurant" || initialRole === "driver") {
      navigate(PARTNER_PATHS[initialRole], { replace: true });
    }
  }, [initialRole, navigate]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Client OAuth Google « NelDaari » — code échangé côté backend (voir utils/googleOauth.js)
  const handleGoogle = () => {
    if (isNativeApp) { startNativeGoogleLogin(); return; }
    startGoogleLogin();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidNational(phoneCode, form.phone)) {
      toast.error(t("reg.invalidPhone", { code: phoneCode }));
      return;
    }
    setLoading(true);
    try {
      const cleaned = {
        ...form,
        phone: buildE164(phoneCode, form.phone),
        email: form.email.trim() || null,
      };
      await register(cleaned);
      toast.success(t("reg.welcome"));
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  const partnerRoute = PARTNER_PATHS[form.role]
    ? { path: PARTNER_PATHS[form.role], title: t(`reg.partner.${form.role}.title`), desc: t(`reg.partner.${form.role}.desc`), cta: t(`reg.partner.${form.role}.cta`) }
    : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <Link to="/" className="font-display text-2xl font-semibold inline-flex items-center gap-2 mb-6">
        <img src="/logo-neldaari.png" alt="NelDaari" className="w-8 h-8" /> NelDaari
      </Link>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold">{t("reg.title")}</h1>
      <p className="text-mute mb-6">{t("reg.subtitle")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {ROLES.map((r) => {
          const Icon = r.icon;
          const active = form.role === r.key;
          return (
            <button type="button" key={r.key} onClick={() => set("role", r.key)} data-testid={`role-${r.key}`} className={`text-left rounded-2xl p-4 border-2 ${active ? "border-brand-red bg-[#FFF5F6]" : "border-sand bg-white hover:border-mute"}`}>
              <Icon className={`w-5 h-5 mb-2 ${active ? "text-brand-red" : "text-mute"}`} />
              <div className="font-semibold">{t(`reg.roles.${r.key}.label`)}</div>
              <div className="text-xs text-mute mt-1">{t(`reg.roles.${r.key}.desc`)}</div>
            </button>
          );
        })}
      </div>

      {partnerRoute ? (
        <div data-testid={`partner-redirect-${form.role}`} className="bg-elevated border border-sand rounded-2xl p-6 sm:p-8">
          <div className="w-10 h-10 rounded-full bg-white text-brand-red grid place-items-center mb-3">
            <Info className="w-5 h-5" />
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-semibold mb-2">{partnerRoute.title}</h2>
          <p className="text-mute mb-5">{partnerRoute.desc}</p>
          <Link
            to={partnerRoute.path}
            data-testid={`partner-cta-${form.role}`}
            className="inline-flex items-center gap-2 rounded-full bg-brand-red hover:bg-[#C81F2D] text-white px-5 py-3 font-semibold"
          >
            {partnerRoute.cta} <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-4 text-xs text-mute">
            {t("reg.alreadyPartner")} <Link to="/espace-partenaires" data-testid="partner-goto-login" className="text-brand-red font-semibold">{t("reg.partnerLogin")}</Link>
          </p>
        </div>
      ) : (
        <>
          <button onClick={handleGoogle} data-testid="register-google-btn" className="w-full mb-3 rounded-full border border-sand bg-white hover:bg-elevated py-3 font-medium inline-flex items-center justify-center gap-2">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.5 5.7 28.9 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.5 6.7 28.9 5 24 5 16.3 5 9.7 9.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c4.8 0 9.2-1.6 12.6-4.4l-5.8-4.9C28.7 36 26.5 37 24 37c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l5.8 4.9C40.2 35.4 44 30.1 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
            {t("auth.continueGoogle")}
          </button>
          <AppleLoginButton label={t("auth.continueApple")} />
          <div className="text-center text-xs uppercase tracking-widest text-mute my-2">{t("common.or")}</div>

          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("reg.fullName")}</label>
                <div className="flex items-center gap-2 bg-elevated rounded-xl px-3">
                  <User className="w-4 h-4 text-mute" />
                  <input data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="w-full bg-transparent py-3 outline-none" placeholder="Fatou Diallo" />
                </div>
              </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("reg.phone")} *</label>
              <PhoneInput
                code={phoneCode}
                onCodeChange={setPhoneCode}
                value={form.phone}
                onChange={(v) => set("phone", v)}
                testId="register-phone"
                required
              />
              <p className="text-[11px] text-mute mt-1">{t("reg.phoneHint")}</p>
            </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("reg.emailOptional")}</label>
              <div className="flex items-center gap-2 bg-elevated rounded-xl px-3">
                <Mail className="w-4 h-4 text-mute" />
                <input data-testid="register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-transparent py-3 outline-none" placeholder={t("reg.emailPh")} autoComplete="email" autoCapitalize="off" autoCorrect="off" spellCheck="false" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("reg.password")}</label>
              <div className="flex items-center gap-2 bg-elevated rounded-xl px-3">
                <Lock className="w-4 h-4 text-mute" />
                <input data-testid="register-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="w-full bg-transparent py-3 outline-none" placeholder="••••••••" autoComplete="new-password" autoCapitalize="off" autoCorrect="off" spellCheck="false" />
              </div>
            </div>
            <button type="submit" data-testid="register-submit" disabled={loading} className="w-full rounded-full bg-brand-red hover:bg-[#C81F2D] text-white py-3 font-semibold disabled:opacity-60">
              {loading ? t("reg.creating") : t("reg.createBtn")}
            </button>
          </form>
          <p className="mt-6 text-sm text-mute text-center">
            {t("reg.already")} <Link to="/login" data-testid="goto-login" className="text-brand-red font-semibold">{t("reg.loginLink")}</Link>
          </p>
        </>
      )}
    </div>
  );
}
